import { CartUpdatedEvent } from '@theme/events';

/**
 * Quiz guidé : enchaîne des questions, additionne les mots-clés des réponses
 * choisies, puis affiche le résultat qui en partage le plus.
 */
class QuizComponent extends HTMLElement {
  connectedCallback() {
    this.intro = this.querySelector('[data-quiz-intro]');
    this.stage = this.querySelector('[data-quiz-stage]');
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.results = Array.from(this.querySelectorAll('[data-quiz-result]'));

    this.progress = this.querySelector('[data-quiz-progress]');
    this.progressFill = this.querySelector('[data-quiz-progress-fill]');
    this.stepLabel = this.querySelector('[data-quiz-step-label]');
    this.backButton = this.querySelector('[data-quiz-back]');

    this.nav = this.querySelector('[data-quiz-nav]');
    this.nextButton = this.querySelector('[data-quiz-next]');
    this.nextLabel = this.querySelector('[data-quiz-next-label]');

    this.outcome = this.querySelector('[data-quiz-outcome]');
    this.outcomeBody = this.querySelector('[data-quiz-outcome-body]');
    this.restartButton = this.querySelector('[data-quiz-restart]');
    this.startButton = this.querySelector('[data-quiz-start]');
    this.copyButton = this.querySelector('[data-quiz-copy]');

    this.autoAdvance = this.dataset.autoAdvance === 'true';
    this.hasIntro = this.dataset.hasIntro === 'true' && this.intro !== null;

    // Une entrée par question : la liste des mots-clés de la réponse choisie.
    this.selections = new Array(this.steps.length).fill(null);
    this.index = 0;

    // Libellé du bouton « voir le produit » : un seul réglage pour tous les résultats.
    if (this.dataset.detailsLabel) {
      this.querySelectorAll('[data-quiz-details-label]').forEach((label) => {
        label.textContent = this.dataset.detailsLabel;
      });
    }

    this.#bindEvents();
    this.#reset();
  }

  disconnectedCallback() {
    clearTimeout(this.advanceTimer);
    clearTimeout(this.copyTimer);
    clearTimeout(this.addedTimer);
  }

  #bindEvents() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  #handleClick = (event) => {
    const answer = event.target.closest('[data-quiz-answer]');
    if (answer && this.contains(answer)) return this.#selectAnswer(answer);

    if (event.target.closest('[data-quiz-start]')) return this.#start();
    if (event.target.closest('[data-quiz-back]')) return this.#goBack();
    if (event.target.closest('[data-quiz-next]')) return this.#goNext();
    if (event.target.closest('[data-quiz-restart]')) return this.#restart();
    if (event.target.closest('[data-quiz-copy]')) return this.#copyCode();

    const addButton = event.target.closest('[data-quiz-add]');
    if (addButton) return this.#addToCart(addButton);
  };

  /** Flèches gauche/droite pour naviguer entre les réponses d'une question. */
  #handleKeydown = (event) => {
    const answer = event.target.closest('[data-quiz-answer]');
    if (!answer) return;

    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();

    const group = Array.from(answer.closest('[role="radiogroup"]').querySelectorAll('[data-quiz-answer]'));
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const next = group[(group.indexOf(answer) + (forward ? 1 : -1) + group.length) % group.length];

    next?.focus();
  };

  // ----- Déroulé -----

  #reset() {
    clearTimeout(this.advanceTimer);
    this.selections.fill(null);
    this.index = 0;

    this.querySelectorAll('[data-quiz-answer]').forEach((answer) => {
      answer.setAttribute('aria-checked', 'false');
    });

    this.results.forEach((result) => {
      result.hidden = true;
    });

    if (this.outcome) this.outcome.hidden = true;

    if (this.hasIntro) {
      this.intro.hidden = false;
      this.stage.hidden = true;
      if (this.progress) this.progress.hidden = true;
      if (this.nav) this.nav.hidden = true;
    } else {
      if (this.intro) this.intro.hidden = true;
      this.#showStep(0);
    }
  }

  #start() {
    if (this.intro) this.intro.hidden = true;
    this.#showStep(0);
  }

  #showStep(index) {
    if (!this.steps.length) return;

    this.index = Math.max(0, Math.min(index, this.steps.length - 1));

    this.stage.hidden = false;
    this.steps.forEach((step, i) => {
      step.hidden = i !== this.index;
    });

    if (this.progress) this.progress.hidden = false;
    this.#updateProgress();
    this.#updateNav();
  }

  #updateProgress() {
    if (!this.progress) return;

    const total = this.steps.length;
    const current = this.index + 1;

    if (this.stepLabel) {
      const template = this.dataset.stepLabel || '';
      this.stepLabel.textContent = template.replace('[etape]', current).replace('[total]', total);
    }

    if (this.progressFill) {
      this.progressFill.style.width = `${(current / total) * 100}%`;
    }

    if (this.backButton) {
      this.backButton.hidden = this.index === 0 && !this.hasIntro;
    }
  }

  #updateNav() {
    if (!this.nav || !this.nextButton) return;

    if (this.autoAdvance) {
      this.nav.hidden = true;
      return;
    }

    this.nav.hidden = false;
    this.nextButton.disabled = this.selections[this.index] === null;

    if (this.nextLabel) {
      const isLast = this.index === this.steps.length - 1;
      this.nextLabel.textContent = isLast
        ? this.dataset.finishLabel || 'Voir ma recommandation'
        : this.dataset.nextLabel || 'Suivant';
    }
  }

  #selectAnswer(answer) {
    const group = answer.closest('[role="radiogroup"]');

    group.querySelectorAll('[data-quiz-answer]').forEach((item) => {
      item.setAttribute('aria-checked', item === answer ? 'true' : 'false');
    });

    this.selections[this.index] = this.#parseKeywords(answer.dataset.keywords);
    this.#updateNav();

    if (!this.autoAdvance) return;

    clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => this.#goNext(), 280);
  }

  #goNext() {
    if (this.selections[this.index] === null) return;

    if (this.index < this.steps.length - 1) {
      this.#showStep(this.index + 1);
      this.#scrollIntoView();
    } else {
      this.#showOutcome();
    }
  }

  #goBack() {
    clearTimeout(this.advanceTimer);

    if (this.index > 0) {
      this.#showStep(this.index - 1);
    } else if (this.hasIntro) {
      this.stage.hidden = true;
      this.intro.hidden = false;
      if (this.progress) this.progress.hidden = true;
      if (this.nav) this.nav.hidden = true;
    }
  }

  #restart() {
    this.#reset();
    this.#scrollIntoView();
  }

  // ----- Résultat -----

  #parseKeywords(value) {
    return (value || '')
      .split(',')
      .map((keyword) => keyword.trim().toLowerCase())
      .filter(Boolean);
  }

  /** Le résultat qui partage le plus de mots-clés avec les réponses gagne. */
  #pickResult() {
    if (!this.results.length) return null;

    const chosen = this.selections.filter(Boolean).flat();

    let best = null;
    let bestScore = -1;

    this.results.forEach((result) => {
      const keywords = this.#parseKeywords(result.dataset.keywords);
      const matches = keywords.filter((keyword) => chosen.includes(keyword)).length;
      const score = matches + Number(result.dataset.priority || 0) / 10;

      if (score > bestScore) {
        bestScore = score;
        best = result;
      }
    });

    return best;
  }

  #showOutcome() {
    const winner = this.#pickResult();
    if (!this.outcome || !winner) return;

    this.results.forEach((result) => {
      result.hidden = true;
    });

    // On remet les résultats d'un tour précédent à leur place avant d'afficher le nouveau.
    Array.from(this.outcomeBody.children).forEach((node) => this.stage.appendChild(node));

    this.outcomeBody.appendChild(winner);
    winner.hidden = false;

    this.stage.hidden = true;
    if (this.progress) this.progress.hidden = true;
    if (this.nav) this.nav.hidden = true;
    this.outcome.hidden = false;

    this.#scrollIntoView();
  }

  #scrollIntoView() {
    const card = this.querySelector('.quiz__card');
    if (!card) return;

    const top = card.getBoundingClientRect().top;
    if (top >= 0 && top <= 120) return;

    const offset = document.querySelector('header-component')?.offsetHeight || 0;
    window.scrollTo({
      top: window.scrollY + top - offset - 24,
      behavior: 'smooth',
    });
  }

  // ----- Code promo -----

  async #copyCode() {
    const code = this.querySelector('[data-quiz-code]')?.textContent.trim();
    const label = this.querySelector('[data-quiz-copy-label]');
    if (!code || !label) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      return;
    }

    label.textContent = this.dataset.copiedLabel || 'Copié !';
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      label.textContent = this.dataset.copyLabel || 'Copier';
    }, 2000);
  }

  // ----- Ajout au panier -----

  async #addToCart(button) {
    if (button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: button.dataset.variantId, quantity: 1 }],
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) throw new Error(data.description || data.message);

      this.#notifyCart(data);
      this.#confirmAdded(button);
    } catch (error) {
      console.error('Quiz — ajout au panier impossible :', error);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  #notifyCart(data) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
      return;
    }

    const currentCount = document.querySelector('cart-icon')?.getCurrentItemCount();

    document.dispatchEvent(
      new CartUpdatedEvent(
        {},
        {
          itemCount: currentCount != null ? currentCount + 1 : undefined,
          source: 'quiz',
          sections: data.sections,
        },
      ),
    );
  }

  #confirmAdded(button) {
    const label = button.querySelector('[data-quiz-add-label]');
    if (!label) return;

    const original = label.textContent;
    label.textContent = this.dataset.addedLabel || 'Ajouté au panier';

    clearTimeout(this.addedTimer);
    this.addedTimer = setTimeout(() => {
      label.textContent = original;
    }, 2500);
  }

  // ----- Éditeur de thème -----

  /** Quand on sélectionne un bloc dans l'éditeur, on saute à l'étape concernée. */
  showBlock(blockId) {
    const target = this.querySelector(`[id*="${blockId}"], [data-shopify-editor-block*="${blockId}"]`);
    if (!target) return;

    const step = target.closest('[data-quiz-step]');
    if (step) {
      if (this.intro) this.intro.hidden = true;
      this.#showStep(this.steps.indexOf(step));
      return;
    }

    const result = target.closest('[data-quiz-result]');
    if (result) {
      this.results.forEach((item) => {
        item.hidden = true;
      });

      Array.from(this.outcomeBody.children).forEach((node) => this.stage.appendChild(node));

      if (this.intro) this.intro.hidden = true;
      this.stage.hidden = true;
      if (this.progress) this.progress.hidden = true;
      if (this.nav) this.nav.hidden = true;

      this.outcomeBody.appendChild(result);
      result.hidden = false;
      this.outcome.hidden = false;
    }
  }
}

if (!customElements.get('quiz-component')) {
  customElements.define('quiz-component', QuizComponent);
}

// Aperçu dans l'éditeur de thème : suivre le bloc en cours d'édition.
document.addEventListener('shopify:block:select', (event) => {
  const quiz = event.target.closest('quiz-component') || event.target.querySelector?.('quiz-component');
  quiz?.showBlock?.(event.detail?.blockId || event.target.id);
});
